/**
 * Installation-domain types (flow-07 installation wizard & first key).
 * Mirrors contracts/api/v1/installations.yaml and the installation-service
 * `_installation_dict` serializer exactly — no invented field names.
 * Enrichment types (customer / device / product / contract tier) mirror the
 * customer-, device-, product- and payment-service serializers used to
 * hydrate display fields the installation rows do not carry.
 */

export type InstallationStatus =
  | "scheduled"
  | "in_progress"
  | "completed"
  | "verified"
  | "cancelled"
  | "pending_approval"
  | "approved"
  | "correction_requested";

export type ConfirmationMethod = "signature" | "pin";

export type InstallationPhotoType =
  | "before"
  | "during"
  | "after"
  | "verification"
  | "installed_device"
  | "serial_closeup";

export interface InstallationPhoto {
  id: number;
  photo_type: InstallationPhotoType;
  photo_url: string;
  captured_at: string | null;
}

/** `_installation_dict` (photos included on detail / evidence responses). */
export interface Installation {
  id: number;
  tenant_id: number | null;
  customer_id: number;
  device_id: number | null;
  contract_id: number | null;
  product_id: number | null;
  technician_id: number | null;
  status: InstallationStatus;
  scheduled_date: string | null;
  completed_date: string | null;
  verified_date: string | null;
  gps_latitude: number | null;
  gps_longitude: number | null;
  gps_accuracy_m: number | null;
  gps_distance_from_registered_m: number | null;
  notes: string | null;
  verified_by_id: number | null;
  rejection_reason: string | null;
  // flow-07 wizard fields
  survey_lat: number | null;
  survey_lng: number | null;
  survey_distance_m: number | null;
  serial_confirmed: boolean;
  serial_confirmed_at: string | null;
  customer_confirmed: boolean;
  customer_confirmed_at: string | null;
  confirmation_method: ConfirmationMethod | null;
  approved_by_id: number | null;
  approved_at: string | null;
  first_token_issued_at: string | null;
  first_token_masked: string | null;
  first_token_delivered_at: string | null;
  correction_reasons: string[] | null;
  created_at: string;
  photos?: InstallationPhoto[];
}

export interface InstallationListResponse {
  installations: Installation[];
  total: number;
  page: number;
  per_page: number;
}

export interface InstallationDetailResponse {
  installation: Installation;
}

/** POST /installations/:id/evidence request body (incremental). */
export interface EvidenceRequest {
  gps_lat?: number;
  gps_lng?: number;
  gps_accuracy_m?: number;
  serial_number?: string;
  customer_confirmed?: boolean;
  confirmation_method?: ConfirmationMethod;
}

export interface EvidenceResponse {
  message: string;
  installation: Installation;
}

export interface InstallationPhotoUploadResponse {
  message: string;
  photo: {
    id: number;
    photo_type: InstallationPhotoType;
    photo_url: string;
    file_size_kb: number;
  };
}

export interface InstallationSubmitResponse {
  message: string;
  installation: Installation;
  reference: string;
}

/** 422 submit body: missing evidence / ledger fail-closed / geofence. */
export interface InstallationSubmitError {
  error: string;
  missing?: string[];
  ledger_unreachable?: boolean;
  payment?: { amount_paid?: number; required_deposit_min_tzs?: number; error?: string };
  geofence?: { distance_m: number; limit_m: number };
}

export interface InstallationApproveResponse {
  message: string;
  installation: Installation;
  reference: string;
  /** FULL token — returned only by approve (incl. idempotent replay). */
  first_token: string;
  masked_token: string;
  valid_days: number;
  dmrv?: { step4_approved: boolean; step5_auto_approved: boolean };
  replayed?: boolean;
}

/** Canonical correction reasons (installations.yaml). */
export const INSTALLATION_CORRECTION_REASONS = [
  "gps_mismatch",
  "photo_unclear",
  "payment_unverified",
  "serial_mismatch",
  "other",
] as const;
export type InstallationCorrectionReason = (typeof INSTALLATION_CORRECTION_REASONS)[number];

export const INSTALLATION_CORRECTION_REASON_LABELS: Record<InstallationCorrectionReason, string> = {
  gps_mismatch: "GPS mismatch",
  photo_unclear: "Photo unclear",
  payment_unverified: "Payment unverified",
  serial_mismatch: "Serial mismatch",
  other: "Other",
};

export interface InstallationCorrectionRequest {
  reasons: InstallationCorrectionReason[];
  note?: string;
}

export interface InstallationSimpleResponse {
  message: string;
  installation: Installation;
  reference: string;
}

// ── Enrichment shapes (display hydration; other services' serializers) ──────

/** GET /api/v1/customers/:id (customer-service `get_customer`). */
export interface CustomerDetail {
  id: number;
  customer_number: string;
  first_name: string;
  last_name: string;
  ward: string | null;
  district: string | null;
  phone: string;
  latitude: number | null;
  longitude: number | null;
  devices: { id: number; serial_number: string; status: string; is_locked: boolean }[];
  active_contract: {
    id: number;
    contract_number: string;
    final_price: number;
    amount_paid: number;
    amount_remaining: number;
    arrears: number;
    expected_payment_amount: number;
    current_monthly_rate: number;
    payment_period_months: number;
    tier_id: number | null;
    is_defaulted: boolean;
    advance_required?: boolean;
    initial_deposit?: number;
    discount_percent?: number;
  } | null;
}

export interface CustomerDetailResponse {
  customer: CustomerDetail;
}

/** GET /api/v1/devices/<serial> (device-service `get_device`). */
export interface DeviceLookupResponse {
  device: {
    id: number;
    serial_number: string;
    model: string | null;
    status: string;
    is_locked: boolean;
    manufacturer_name: string | null;
    device_type_name: string | null;
    customer: { id: number; customer_number: string; name: string; phone: string } | null;
  };
}

/** GET /api/v1/products/:id (product-service). */
export interface ProductDetailResponse {
  product: { id: number; name: string };
}

/** GET /api/v1/devices list rows (device-service `list_devices`). */
export interface DeviceListRow {
  id: number;
  serial_number: string;
  model: string | null;
  status: string;
}

export interface DeviceListResponse {
  devices: DeviceListRow[];
  total: number;
}

/** GET /api/v1/users list rows (auth-service `list_users`). */
export interface UserListRow {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  role: string;
}

export interface UserListResponse {
  users: UserListRow[];
  total: number;
}

/** GET /api/v1/contracts/:id/tier-info (payment-service). */
export interface ContractTierInfoResponse {
  contract_id: number;
  contract_number: string;
  pricing: { current_contract_price: number; current_monthly_rate: number };
  tier: { id: number | null; name: string; duration_months: number; discount_percent: number };
  status: {
    amount_paid: number;
    amount_remaining: number;
    is_completed: boolean;
    is_defaulted: boolean;
    escalation_count: number;
  };
}
